// Types for the live elemis payment-schedule preview (#475-A / issue #595).
// See docs/integrations/elemis-payment-schedule-contract.md §5/§6 and
// docs/plans/2026-07-14-payment-schedule-options.md (Phase A).

import type { DateKey } from '../utils/dateKey';

export type ScheduleFrequency =
    | 'daily'
    | 'weekly'
    | 'fortnightly'
    | 'monthly'
    | 'bi_monthly'
    | 'quarterly'
    | 'yearly';

// Reserved for Phase D (item_based); typed now so the boundary shape is
// stable, not wired into any request in Phase A.
export interface CollectionTarget {
    amount: number; // integer minor units
    byPeriod?: number;
    byDate?: string;
}

/**
 * camelCase boundary shape accepted by `POST /quotes/schedule/preview`
 * (contract §5, backend/quote-service/app/Services/SchedulePreviewService.php).
 * All amounts are integer minor units by the time they reach this shape —
 * conversion happens once, at the `schedulePreview.ts` service boundary
 * (D12 of the plan).
 */
export interface SchedulePreviewParams {
    amount: number;
    hasDeposit?: boolean;
    depositAmount?: number;
    numInstalments?: number;
    frequency?: ScheduleFrequency;
    collectionTargets?: CollectionTarget[];
    /** Minor units. The backend nets this off `amount` and forces `hasDeposit`
     *  false, so it never itself becomes an elemis wire field. */
    dealerCollectedDepositAmount?: number;
}

/** One instalment row from the elemis preview response (contract §6). */
export interface ScheduleRow {
    number: number;
    date: string; // YYYY-MM-DD
    payment: number;
    principal: number;
    interest: number;
    balance: number;
}

/**
 * Schedule-level totals from the elemis preview response (contract §6).
 * Field names are passed through as elemis/the backend return them
 * (`SchedulePreviewService::normaliseResponse` does not rename them) —
 * only the top-level `num_payments` is renamed to `numPayments`.
 */
export interface ScheduleMeta {
    total_repayable: number;
    total_interest: number;
    total_principal: number;
    total_fees: number;
    balloon: number;
}

export interface SchedulePreview {
    schedule: ScheduleRow[];
    meta: ScheduleMeta;
    numPayments: number;
}

/** A SIBLING of `params` on `PaymentSelection`: `params` is the exact
 *  elemis-preview request shape and has no slot for a payment record. */
export interface DealerCollectedDeposit {
    amountMinor: number;
    reference: string;
    invoiceNumber?: string;
    collectedAt: DateKey;
}

/** Persisted asymmetrically: `depositCollection` only as `'dealer'` (absence means Elemis
 *  collects), `fullPaymentCollection` always (absence means the legacy born-Active sale). */
export interface CollectionRouting {
    depositCollection?: 'elemis' | 'dealer';
    fullPaymentCollection?: 'elemis' | 'dealer';
    dealerPayment?: DealerCollectedDeposit;
}

/**
 * The salesperson's confirmed payment-schedule choice for this quote
 * (Phase B / #475-B, docs/plans/2026-07-14-payment-schedule-options.md).
 * Supersedes the legacy `PaymentScheduleOptions` shape for gating and
 * persisting the payment step: `methodKey` identifies which
 * `SchedulingMethod` was chosen, `params` is the exact preview request that
 * produced `snapshot` (so a confirmed schedule can be distinguished from a
 * later, unconfirmed live-preview refetch), and `snapshot` is the
 * elemis-computed schedule at confirmation time.
 *
 * This is journey state — it lives in the persisted Redux `quote` slice
 * (D2), not React Query. The live, in-progress preview stays in React Query
 * and is never written here until the salesperson explicitly confirms.
 */
export interface PaymentSelection {
    methodKey: string;
    params: SchedulePreviewParams;
    snapshot: SchedulePreview;
    /**
     * #728: the collection lead (whole months) that produced `params`. Deliberately
     * a SIBLING of `params`, never a member of it — `SchedulePreviewParams` is the
     * exact elemis request shape (contract §5) and `PreviewScheduleRequest` validates
     * exactly those six fields, so a seventh has nowhere to go (#727 D10, leg 3).
     *
     * OPTIONAL and NOT part of `isValidPaymentSelection`'s required set: every
     * selection persisted before #728 lacks it, and a selection on a method that
     * does not honour the lead deliberately omits it rather than recording a number
     * that did not shape the schedule.
     */
    collectionLeadMonths?: number;
    /**
     * #767: the artemis-local calendar date the preview request behind
     * `snapshot` was built on — `todayDateKey()`, the same anchor #740's term
     * derivation uses. A SIBLING of `params`, never a member of it: `params` is
     * the exact elemis request shape (contract §5) and `PreviewScheduleRequest`
     * validates exactly those six fields, so a seventh has nowhere to go
     * (#728 D10, leg 3).
     *
     * Recorded for EVERY method, unlike `collectionLeadMonths` — every
     * schedule elemis returns is anchored to its request date (contract §8
     * B1/B6), so this is never a number that did not shape the schedule.
     *
     * OPTIONAL and deliberately NOT part of `isValidPaymentSelection`'s
     * required set: every selection confirmed before #767 lacks it, and such a
     * selection must resume and render exactly as it does today.
     *
     * Its consumers are `resolveAppliedDeposit` (`services/appliedDeposit.ts`)
     * and, since #766, `resolveFirstCollectionDate`
     * (`services/firstCollectionDate.ts`). It must NOT be added to the
     * confirmed-vs-live comparison in `PaymentStep` — see the plan's D6.
     */
    scheduleAnchorDate?: DateKey;
    /** OPTIONAL and outside `isValidPaymentSelection`'s required set. Absence reads
     *  per-axis: Elemis collects the deposit, but a legacy born-Active full payment. */
    collectionRouting?: CollectionRouting;
}

/**
 * Runtime guard for `PaymentSelection` (redux-persist hydrate guard, D9).
 * Persisted state from a pre-#475-B session simply lacks `paymentSelection`
 * (the field is optional, so that alone never crashes), but this guard is
 * the belt-and-braces check every READ site uses before trusting the shape
 * — it protects against a partial/malformed object ever satisfying the
 * `payment` step gate or being dereferenced (`.snapshot.schedule`, etc.)
 * without first confirming the fields it needs actually exist.
 */
export const isValidPaymentSelection = (value: unknown): value is PaymentSelection => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as Partial<PaymentSelection>;
    return (
        typeof candidate.methodKey === 'string' &&
        candidate.methodKey.length > 0 &&
        // `params` is required by the type and is dereferenced by callers
        // (e.g. the confirmed-vs-live comparison in PaymentStep), so a
        // partial/legacy object without a real params object must never
        // pass. `typeof null === 'object'`, hence the explicit null check.
        typeof candidate.params === 'object' &&
        candidate.params !== null &&
        typeof candidate.snapshot === 'object' &&
        candidate.snapshot !== null &&
        Array.isArray(candidate.snapshot.schedule) &&
        // `snapshot.meta` must be a real object (callers read
        // `snapshot.meta.total_*`); a truthy non-object (e.g. a string)
        // used to slip through the old `!!candidate.snapshot.meta` check.
        typeof candidate.snapshot.meta === 'object' &&
        candidate.snapshot.meta !== null &&
        typeof candidate.snapshot.numPayments === 'number'
    );
};
